// Printed Wrap feature - shared schemas for the print-designs module
// (custom-printed vinyl wraps, distinct from a plain color wrap). Mirrors the
// mockups schema's shape conventions (id/workspaceId/createdAt/updatedAt,
// signed url + thumbUrl on read responses) so the server/client patterns
// already used for mockups carry over with minimal new conventions.
import { z } from 'zod';
import { SIZE_CLASSES } from '../constants.js';
import { uuidSchema } from './common.js';
// ---------------------------------------------------------------------------
// Vehicle template - per-view panel layout in inches, shared by every design
// for a given vehicle (year|make|model) in a workspace.
// ---------------------------------------------------------------------------
// 'roof' is the full plan (top-down) view - hood + roof + trunk/rear deck -
// there is no separate 'hood' view; the key stays 'roof' for data
// compatibility, but its label everywhere is "Top" (see
// normalizePlacements, which folds any legacy 'hood' placement into it).
export const PRINT_VIEWS = ['driver_side', 'passenger_side', 'front', 'rear', 'roof'];
// Inches, origin top-left of the view box. x/y/w/h are the panel's bounding
// box - the rectangle actually cut from the roll - and MUST equal the
// bounding box of `points` when present. `points` is the panel's true
// outline (a closed polygon, no repeated first point) for a panel whose
// real shape follows a curve (a fender around a wheel arch, a sloped hood
// edge); a panel that is already an exact rectangle omits it.
export const printPanelSchema = z.object({
    key: z.string().min(1).max(40),
    label: z.string().min(1).max(60),
    view: z.enum(PRINT_VIEWS),
    x: z.number(),
    y: z.number(),
    w: z.number().positive(),
    h: z.number().positive(),
    points: z.array(z.tuple([z.number(), z.number()])).min(3).optional(),
});
// One side's headlight/tail-light size and position (mirrored for the
// other side), and the grille/plate recesses - measured (AI measurement)
// when available, else an archetype default (see print/templates.ts).
// Treated as exclusion zones exactly like glass: panels clear them, and
// they draw on top of artwork in the editor, preview and AI layouts.
const printFeatureRectSchema = z.object({
    widthIn: z.number().positive(),
    heightIn: z.number().positive(),
    centerFromCenterlineIn: z.number().min(0),
    bottomFromGroundIn: z.number().positive(),
});
const printPlateFeatureSchema = z.object({
    widthIn: z.number().positive(),
    heightIn: z.number().positive(),
    bottomFromGroundIn: z.number().positive(),
});
export const printFeaturesSchema = z.object({
    headlight: printFeatureRectSchema,
    taillight: printFeatureRectSchema,
    grille: z.object({ widthIn: z.number().positive(), heightIn: z.number().positive(), bottomFromGroundIn: z.number().positive() }),
    frontPlate: printPlateFeatureSchema.optional(),
    rearPlate: printPlateFeatureSchema.optional(),
});
// The four AI-drawn outline views (PRINT_WRAP_SPEC.md "AI-drawn vehicle
// outline") - passenger_side is the driver_side drawing mirrored
// horizontally, so only these four are ever generated (see outlineViewFor in
// print/outline.ts).
export const PRINT_OUTLINE_VIEWS = ['driver_side', 'front', 'rear', 'roof'];
// One generated view's drawing: three PNGs (a clean quantized image, a
// printable-area mask, and a line-art layer), sized in both pixels and
// inches at a scale consistent across every view (heightIn always equals
// the template's overall.heightIn - see print/outline.ts). bodyBoxIn is the
// printable body area's bounding box within this view, in inches - it
// becomes the single 'body' panel for this view (applyOutlineToTemplate).
export const printOutlineViewSchema = z.object({
    widthIn: z.number().positive(),
    heightIn: z.number().positive(),
    widthPx: z.number().int().positive(),
    heightPx: z.number().int().positive(),
    imageMediaId: uuidSchema,
    maskMediaId: uuidSchema,
    linesMediaId: uuidSchema,
    bodyBoxIn: z.object({ x: z.number(), y: z.number(), w: z.number().positive(), h: z.number().positive() }),
});
// Shape of one outline's generated views, shared by the active `outline`
// field and each `outlineLibrary` entry below.
const printOutlineViewsSchema = z
    .object({ driver_side: printOutlineViewSchema, front: printOutlineViewSchema, rear: printOutlineViewSchema, roof: printOutlineViewSchema })
    .partial();
// One previously generated outline, kept around after a redraw or a switch
// to the default outline replaces it as the active one, so a shop can pick
// it again later for free instead of losing a paid-for drawing. `id` is the
// requestedAt ISO string of the generation that produced it (or a uuid for
// one created another way) - stable and unique per entry.
export const printOutlineLibraryEntrySchema = z.object({
    id: z.string().min(1).max(80),
    createdAt: z.string(),
    views: printOutlineViewsSchema.default({}),
});
// Live step-by-step progress for a 'pending' outline generation, kept in an
// in-memory map only (server/print-designs/outline.ts) and injected into the
// DTO while a generation is in flight - never persisted to the row, since the
// four view steps update it in parallel and a read-modify-write on the stored
// template would race. 'measure' only appears when this generation actually
// needs an AI measurement first. Absent once the process that ran the
// generation restarts (no in-memory entry survives that) or once status
// leaves 'pending' - the client falls back to a time-based progress UI then.
export const printOutlineProgressStepSchema = z.object({
    key: z.enum(['measure', 'driver_side', 'front', 'rear', 'roof']),
    status: z.enum(['waiting', 'running', 'done', 'retrying']),
});
export const printOutlineProgressSchema = z.object({
    steps: z.array(printOutlineProgressStepSchema),
    startedAt: z.string(),
});
export const printTemplateSchema = z.object({
    units: z.literal('in'),
    overall: z.object({
        lengthIn: z.number().positive(),
        widthIn: z.number().positive(),
        heightIn: z.number().positive(),
        wheelbaseIn: z.number().positive(),
    }),
    archetype: z.enum(['sedan', 'coupe', 'hatch', 'suv', 'pickup', 'van']),
    doors: z.union([z.literal(2), z.literal(4)]),
    views: z.record(z.enum(PRINT_VIEWS), z.object({ widthIn: z.number().positive(), heightIn: z.number().positive() })),
    panels: z.array(printPanelSchema).min(1),
    notes: z.string().max(2000).default(''),
    // Bumps whenever print/templates.ts's generator geometry changes in a way
    // that matters for rebuilding a saved template server side. Templates
    // saved before this field existed have no value and count as version 1.
    generatorVersion: z.number().int().optional(),
    // Measured (AI measurement) or archetype-default headlight/tail-light/
    // grille/plate geometry. Absent on templates saved before this existed
    // or measured before it was collected - print/templates.ts falls back to
    // its own archetype defaults in that case.
    features: printFeaturesSchema.optional(),
    // AI-generated vehicle-accurate outline drawing, the only outline option -
    // requested via POST .../template/outline, which now also fires
    // automatically the first time a design is created for a vehicle with no
    // outline yet (see createPrintDesign in server/print-designs/service.ts).
    // Absent until the first generation starts. 'pending'/'failed' carry no
    // views yet (or a partial set from a run that died mid-way), except a
    // redraw's 'pending' state, which carries the previous ready outline's
    // views forward so it stays applied while the redraw runs (see
    // activeOutlineViews in print/outline.ts). 'declined' is legacy only (from
    // when a default parametric outline could be chosen instead) - parsed for
    // back compat and treated exactly like no outline everywhere. 'ready'
    // applies each view independently once it's present
    // (applyOutlineToTemplate): driver_side/passenger_side/front/rear together
    // once all three of driver_side/front/rear exist, and roof on its own once
    // present - a template saved before roof outlines existed simply has no
    // roof view and keeps the parametric roof.
    outline: z
        .object({
        status: z.enum(['pending', 'ready', 'failed', 'declined']),
        requestedAt: z.string(),
        error: z.string().max(500).optional(),
        // Which outlineLibrary entry a 'ready' outline is - optional for back
        // compat with rows saved before outlineLibrary existed (see
        // normalizeOutlineLibraryTemplate in print/outline.ts).
        activeOutlineId: z.string().optional(),
        views: printOutlineViewsSchema.default({}),
        // Whether this generation was free - the first successful generation
        // for this workspace+vehicle (see isFreeOutlineGeneration in
        // print/outline.ts). Set on the pending state so the client can show it
        // wasn't charged; absent on older rows.
        free: z.boolean().optional(),
        // Live step-by-step progress while status is 'pending' - see
        // printOutlineProgressSchema above. Injected server-side, never stored.
        progress: printOutlineProgressSchema.optional(),
    })
        .optional(),
    // Every successfully generated outline for this vehicle, newest first,
    // capped at 10 - kept so a redraw or a switch to the default outline never
    // throws away a previously paid-for drawing (see print/outline.ts). Absent
    // on templates saved before this existed; a still-'ready' outline from
    // before then is normalized into this list lazily (see
    // normalizeOutlineLibraryTemplate) rather than migrated up front.
    outlineLibrary: z.array(printOutlineLibraryEntrySchema).max(10).optional(),
});
// Crop rectangle expressed as fractions of the SOURCE artwork image (0..1).
export const printCropSchema = z.object({
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    w: z.number().gt(0).max(1),
    h: z.number().gt(0).max(1),
});
export const FULL_CROP = { x: 0, y: 0, w: 1, h: 1 };
// A single artwork layer placed on a view. Layer z-order within a view is
// array order (index 0 = bottom); multiple placements per view are allowed.
export const printPlacementSchema = z.object({
    id: z.string().min(1).max(40),
    artworkMediaId: uuidSchema.nullable(), // null only on legacy rows before normalization
    view: z.enum(PRINT_VIEWS),
    x: z.number(),
    y: z.number(),
    w: z.number().positive(),
    h: z.number().positive(),
    rotationDeg: z.number().min(-180).max(180).default(0),
    flipX: z.boolean().default(false),
    flipY: z.boolean().default(false),
    crop: printCropSchema.default(FULL_CROP),
});
// Accepts legacy placement objects saved before id/artworkMediaId/flip/crop
// existed: fills a stable id (`layer-${index}`) when missing, defaults
// artworkMediaId to the design's fallback artwork when missing, fills
// flip/crop defaults, and folds a legacy `view: 'hood'` (removed - the hood
// is now part of the 'roof' plan view) into 'roof', keeping x unchanged (y
// is unaffected since 'roof' is now the same top-down coordinate space the
// hood used to have its own view for). Entries that fail to parse are
// dropped.
export function normalizePlacements(raw, fallbackArtworkMediaId) {
    if (!Array.isArray(raw))
        return [];
    const out = [];
    raw.forEach((entry, index) => {
        if (typeof entry !== 'object' || entry === null)
            return;
        const record = entry;
        const candidate = {
            ...record,
            id: record.id ?? `layer-${index}`,
            artworkMediaId: record.artworkMediaId ?? fallbackArtworkMediaId,
            view: record.view === 'hood' ? 'roof' : record.view,
        };
        const parsed = printPlacementSchema.safeParse(candidate);
        if (parsed.success)
            out.push(parsed.data);
    });
    return out;
}
export const printArtworkSchema = z.object({
    id: uuidSchema,
    url: z.string(),
    thumbUrl: z.string().nullable(),
    width: z.number().int().nullable(),
    height: z.number().int().nullable(),
    // Uploaded as vector (SVG): prints sharp at any size, so it never counts
    // toward a panel's effective resolution. width/height are then the
    // rasterized proxy's, used only for the aspect ratio.
    isVector: z.boolean().optional(),
    createdAt: z.string(),
});
export const printTemplateSourceSchema = z.enum(['builtin', 'ai', 'uploaded']);
export const printVehicleTemplateSchema = z.object({
    id: uuidSchema,
    workspaceId: uuidSchema,
    vehicleKey: z.string(),
    catalogVehicleId: uuidSchema.nullable(),
    workspaceVehicleId: uuidSchema.nullable(),
    sizeClass: z.enum(SIZE_CLASSES),
    source: printTemplateSourceSchema,
    template: printTemplateSchema,
    verifiedAt: z.string().nullable(),
    uploadMediaId: uuidSchema.nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
// ---------------------------------------------------------------------------
// Print design - one customer/vehicle's artwork placement + renders.
// ---------------------------------------------------------------------------
export const printDesignStatusSchema = z.enum(['draft', 'rendered', 'final']);
export const printRenderStatusSchema = z.enum(['pending', 'succeeded', 'failed']);
// 'side'/'front'/'rear' are the current render-set angles (a straight
// orthographic view produced from our own layout PNG); 'three_quarter_front'
// and 'three_quarter_rear' are legacy angles kept only so old rows still
// validate and display with their original caption.
export const printRenderAngleSchema = z.enum(['side', 'front', 'rear', 'three_quarter_front', 'three_quarter_rear']);
export const printDesignRenderSchema = z.object({
    id: uuidSchema,
    designId: uuidSchema,
    quality: z.enum(['standard', 'hd']),
    angle: printRenderAngleSchema,
    status: printRenderStatusSchema,
    creditsSpent: z.number().int(),
    outputMediaId: uuidSchema.nullable(),
    url: z.string().nullable(),
    thumbUrl: z.string().nullable(),
    error: z.string().nullable(),
    createdAt: z.string(),
    completedAt: z.string().nullable(),
    // Groups the three renders of a set (side, three_quarter_front,
    // three_quarter_rear) generated from one Generate click - null for a
    // legacy render or a single customer-photo-mode render.
    batchId: uuidSchema.nullable(),
    side: z.enum(['driver', 'passenger']).nullable(),
});
export const printDesignSchema = z.object({
    id: uuidSchema,
    workspaceId: uuidSchema,
    name: z.string().min(1).max(120),
    status: printDesignStatusSchema,
    vehicleKey: z.string(),
    vehicleYear: z.number().int().nullable(),
    vehicleMake: z.string(),
    vehicleModel: z.string(),
    vehicleLabel: z.string(),
    sizeClass: z.enum(SIZE_CLASSES),
    catalogVehicleId: uuidSchema.nullable(),
    catalogTrimId: uuidSchema.nullable(),
    workspaceVehicleId: uuidSchema.nullable(),
    templateId: uuidSchema,
    template: printVehicleTemplateSchema,
    coverageLevel: z.enum(['full', 'three_quarter', 'half', 'spot']),
    vehicleColor: z.string().max(60),
    vehiclePhotoMediaId: uuidSchema.nullable(),
    vehiclePhotoUrl: z.string().nullable(),
    artworkMediaId: uuidSchema.nullable(),
    artworkUrl: z.string().nullable(),
    artworks: z.array(printArtworkSchema),
    placements: z.array(printPlacementSchema),
    renders: z.array(printDesignRenderSchema),
    latestRenderUrl: z.string().nullable(),
    notes: z.string().max(2000),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const createPrintDesignSchema = z.object({
    name: z.string().min(1).max(120),
    vehicleYear: z.number().int().nullable(),
    vehicleMake: z.string().min(1).max(60),
    vehicleModel: z.string().min(1).max(80),
    vehicleLabel: z.string().min(1).max(160),
    sizeClass: z.enum(SIZE_CLASSES),
    catalogVehicleId: uuidSchema.nullable().default(null),
    catalogTrimId: uuidSchema.nullable().default(null),
    workspaceVehicleId: uuidSchema.nullable().default(null),
    vehicleColor: z.string().trim().max(60).default(''),
    vehiclePhotoMediaId: uuidSchema.nullable().default(null),
});
export const updatePrintDesignSchema = z
    .object({
    name: z.string().min(1).max(120),
    status: printDesignStatusSchema,
    coverageLevel: z.enum(['full', 'three_quarter', 'half', 'spot']),
    vehicleColor: z.string().trim().max(60),
    vehiclePhotoMediaId: uuidSchema.nullable(),
    artworkMediaId: uuidSchema.nullable(),
    placements: z.array(printPlacementSchema).max(60),
    notes: z.string().max(2000),
})
    .partial();
export const updatePrintTemplateSchema = z.object({
    template: printTemplateSchema,
    verified: z.boolean().optional(),
});
// POST body is empty - the vehicle/trim identity is already on the design.
export const measurePrintTemplateSchema = z.object({});
export const renderPrintDesignSchema = z.object({
    quality: z.enum(['standard', 'hd']),
    angle: printRenderAngleSchema.default('side'),
    layoutMediaIds: z.array(uuidSchema).min(1).max(6),
    useVehiclePhoto: z.boolean().default(true),
    side: z.enum(['driver', 'passenger']).optional(),
    // The three views uploaded for a render set (the chosen side, front and
    // rear), in that order - required for the render-set path; the
    // customer-photo path stays a single render and does not use this.
    setLayouts: z.object({ side: uuidSchema, front: uuidSchema, rear: uuidSchema }).optional(),
    // Fraction of each panel's printable area covered by artwork, measured
    // client-side from the rasterized layouts. Lets the prompt anchor the design
    // to named body panels instead of the model guessing proportions.
    panelCoverage: z
        .array(z.object({ view: z.enum(PRINT_VIEWS), key: z.string().min(1).max(40), coveredFraction: z.number().min(0).max(1) }))
        .max(80)
        .optional(),
});
export const printSpecOptionsSchema = z.object({
    rollWidthIn: z.number().min(24).max(64).default(54),
    bleedIn: z.number().min(0).max(4).default(1),
    overlapIn: z.number().min(0).max(4).default(0.5),
    units: z.enum(['in', 'mm', 'both']).default('both'),
    // Freshly-rasterized layout PNGs to embed one-per-view in the spec PDF -
    // the client re-uploads them right before requesting the spec so the PDF
    // always reflects the current placements, not a stale render.
    layoutMediaIds: z.array(uuidSchema).max(6).default([]),
});
// Options for one panel's print-ready PDF (POST .../panels/:view/:panelKey/
// print-file), the same roll settings the spec card uses for the spec PDF.
export const printPanelFileOptionsSchema = printSpecOptionsSchema.pick({ rollWidthIn: true, bleedIn: true, overlapIn: true });
// Largest raster print artwork upload kept at full resolution, in pixels.
// Bounded by the API instance's memory (512 MB): decoding is ~4 bytes per
// pixel. Vector (SVG) artwork has no pixel limit.
export const PRINT_ARTWORK_MAX_PIXELS = 40_000_000;
// Effective resolution thresholds at full printed size, in pixels per inch.
// Vehicle graphics are read from a few feet away, so 100 ppi is the usual
// shop standard and anything under 60 prints visibly soft.
export const PRINT_PPI_GOOD = 100;
export const PRINT_PPI_MIN = 60;
export const printDesignListItemSchema = printDesignSchema.pick({
    id: true,
    name: true,
    status: true,
    vehicleKey: true,
    vehicleLabel: true,
    vehicleYear: true,
    vehicleMake: true,
    vehicleModel: true,
    sizeClass: true,
    coverageLevel: true,
    latestRenderUrl: true,
    artworkUrl: true,
    updatedAt: true,
    createdAt: true,
});
/** `${year ?? ''}|${make trimmed+lowercased}|${model trimmed+lowercased}`,
 * with internal whitespace collapsed - the key that groups every design and
 * template for "this exact year/make/model" within a workspace. */
export function printVehicleKey(year, make, model) {
    const norm = (s) => s.trim().toLowerCase().replace(/\s+/g, ' ');
    return `${year ?? ''}|${norm(make)}|${norm(model)}`;
}
//# sourceMappingURL=print.js.map